"use client";

/**
 * ConfirmDeleteDialog
 * A reusable safety-gate dialog for destructive deletes.
 * The user must type "I AM SURE" exactly before the Delete button enables.
 * The item is soft-deleted (moved to 30-day trash) via softDeleteDoc().
 */

import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";

const CONFIRM_PHRASE = "I AM SURE";

interface ConfirmDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;         // e.g. "Delete Release"
  description?: string;  // extra context shown below the title
  itemLabel?: string;    // name of the item being deleted (quoted in dialog)
  onConfirm: () => Promise<void> | void;
}

export function ConfirmDeleteDialog({
  open,
  onOpenChange,
  title,
  description,
  itemLabel,
  onConfirm,
}: ConfirmDeleteDialogProps) {
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ready = phrase.trim().toUpperCase() === CONFIRM_PHRASE;

  const handleConfirm = async () => {
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (e: any) {
      // Show the error inside the dialog so the user knows what happened
      setError(e?.message ?? "An unexpected error occurred. Please try again.");
    } finally {
      setBusy(false);
      setPhrase("");
    }
  };

  const handleOpenChange = (v: boolean) => {
    if (!busy) {
      setPhrase("");
      onOpenChange(v);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            {title}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-2">
              {itemLabel && (
                <p>
                  You are about to delete{" "}
                  <span className="font-semibold text-foreground">&ldquo;{itemLabel}&rdquo;</span>.
                </p>
              )}
              {description && <p>{description}</p>}
              <p className="text-amber-600 dark:text-amber-400 text-sm">
                This item will be moved to trash and permanently deleted after <strong>30 days</strong>.
                You can restore it from Super Admin → Trash before then.
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-2">
          <p className="text-sm font-medium">
            Type <span className="font-bold font-mono tracking-widest">{CONFIRM_PHRASE}</span> to confirm:
          </p>
          <Input
            value={phrase}
            onChange={e => setPhrase(e.target.value)}
            placeholder={CONFIRM_PHRASE}
            className={ready ? "border-destructive ring-1 ring-destructive" : ""}
            onKeyDown={e => { if (e.key === "Enter" && ready) handleConfirm(); }}
            autoFocus
          />
        </div>

        {error && (
          <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2 mt-2">{error}</p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!ready || busy}
          >
            {busy
              ? <Loader2 className="h-4 w-4 animate-spin mr-1" />
              : <Trash2 className="h-4 w-4 mr-1" />}
            Delete permanently
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
